import { useIonRouter } from "@ionic/react";
import { SwingHeroImageWrapper } from "components";
import { SubData } from "pages";
import { createSubMapProfilePath } from "routes/routeData";
import { Card, Colors, Icon, IconName } from "swing-components";

import { translate } from "~utils";

import styles from "./SubMapCard.module.css";

// Ripped from Icon.tsx
type ValuesOf<T extends object> = T[keyof T];

type SubMapCardProps = {
  sub: SubData;
};

export function SubMapCard(props: SubMapCardProps) {
  const { sub } = props;
  const subProfileUrl = createSubMapProfilePath(sub.id);
  const router = useIonRouter();

  const defaultProfileImage =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAMAAABg3Am1AAAAh1BMVEUAAADA3P/C3v/B3P/A3P++4P/A3P90r//A3P++3P91sP/A3P91sP90sf/A3P91sP+/3f90sP9ysf9/tf92sP92sP/A3P/B3P/B2v92sf++3P/B3P92sf/A3P91sP+ky/+cxv+72v99tf+u0f+w0/+Uwv+Btv95sv+31v+MvP+ozv+fyP+Huv++2gB6AAAAHXRSTlMAfyBg3xDvgL/v789gIJDfn5UQ78+/sI9QUM+vr+MJwjoAAAGOSURBVEjHlZTbdoIwEEVHAogKVuu97SSEO8L/f19XXdUMSSCyn/dZM4eEgEHALt/+ChH96MI8cBAsIhywYd6kvkKT3WhE6RoLq+5tcBTfMoStcILwaqyDDhaTfi7SVBQTiSsSZFrzB1knkcBIX7p/nvEXGZ0SquY+KgSn1IIeoq1AzjUKs4aHiibTA5UkSwWPwA4VHTdI9REeEjIzkKE2gukNXC2+tE9kIIgQaRultkBKjQDYvACDLbpWulNjCxESClsgp8YZfHR81gopPiA6SgikhFpAWq8GRQXsR1c3mgAhDhH6Qhrgo0ZRmT8QLX1GHSmqfz2VqBOpgzMegRwtbIHhLBgEOAsPIJrjbyxvnixEWbZt35alyKWxEUAQEvleVnxAXwo52IiOuLc1t9G+LvgO4DGCPJB2srR5DniO6JRuj3T0Pd40PXfSN756jA/8DQ4evIjfCdyAsHT7SxiwdPsat/WUvo7BIPkY948JEd1rrZcwQrK36p8wThIfh/rJ1M3M/vRXZ/3xs49N+xfiWc8K6z8nBgAAAABJRU5ErkJggg==";

  type specialQualsXfItemType = {
    text: string;
    icon: IconName;
    color: ValuesOf<typeof Colors>;
  };

  type specialQualsXfType = Record<string, specialQualsXfItemType>;

  const specialQualsXf: specialQualsXfType = {
    isStarSub: { text: "Star Sub", icon: "Star", color: Colors.blue500 },
    isHighlyReliable: { text: "Highly Reliable", icon: "Verified", color: Colors.green500 },
    isAlwaysOnTime: { text: "Always on Time", icon: "Hourglass", color: Colors.purple500 },
    enjoysLongTermRequests: {
      text: "Enjoys Long Term Requests",
      icon: "Calendar",
      color: Colors.blue500,
    },
    isLookingForNewOpportunities: {
      text: "Looking for New Opportunities",
      icon: "Eye On",
      color: Colors.red500,
    },
  };

  const STRING_CUTOFF_LENGTH = 180;

  return (
    <Card onClick={() => router.push(subProfileUrl)}>
      <div className={styles.cardTop}>
        {sub.isSwingHero ? (
          <SwingHeroImageWrapper>
            <img
              alt={sub.name}
              css={{ width: 100, borderRadius: "50px", flexShrink: 0 }}
              src={sub.profileImageUrl ? sub.profileImageUrl : defaultProfileImage}
            />
          </SwingHeroImageWrapper>
        ) : (
          <img
            alt={sub.name}
            css={{ width: 100, borderRadius: "50px", flexShrink: 0 }}
            src={sub.profileImageUrl ? sub.profileImageUrl : defaultProfileImage}
          />
        )}
        <div>
          <h3 className="mb-2 mt-0">{sub.name}</h3>
          <p className="italic m-0">
            {sub.about?.substring(0, STRING_CUTOFF_LENGTH)}{" "}
            {sub.about?.length && sub.about.length > STRING_CUTOFF_LENGTH && "..."}
          </p>
        </div>
        <div className={styles.stats}>
          {Object.keys(sub.specialQualifications).map((item, i) => {
            if (!sub.specialQualifications[item as keyof typeof sub.specialQualifications]) {
              return;
            }
            const qualName = specialQualsXf[item].text;
            const qualIcon = specialQualsXf[item].icon;
            const qualColor = specialQualsXf[item].color;
            return (
              <div className="flex items-center gap-2 mb-1" key={i}>
                <Icon name={qualIcon} color={qualColor} />
                {qualName}
              </div>
            );
          })}
        </div>
      </div>
      <hr className="mt-4 mb-4" />
      {sub.stats && <SubMapExperience stats={sub.stats} />}
    </Card>
  );
}

// This is a repurpose of the SwingExperience component to remove the section wrapper

type SubMapExperienceProps = {
  stats: {
    daysWorked?: number;
    schoolsWorked?: number;
    timesPreferred?: number;
    positiveReviews?: number;
  };
};

function SubMapExperience(props: SubMapExperienceProps) {
  const { stats } = props;

  const subMapKeyXf = {
    daysWorked: translate("SUB_MAP_EXPERIENCE_DAYS_WORKED"),
    schoolsWorked: translate("SUB_MAP_EXPERIENCE_SCHOOLS_WORKED"),
    timesPreferred: translate("SUB_MAP_EXPERIENCE_TIMES_PREFERRED"),
    positiveReviews: translate("SUB_MAP_EXPERIENCE_POSITIVE_REVIEWS"),
  };

  return (
    <div className="px-4 pb-4">
      <h2 className="pt-3 pb-3">{translate("SUB_MAP_SWING_EXPERIENCE")}</h2>
      <ul className={styles.expHolder}>
        {Object.keys(stats).map((item, i) => {
          return (
            <li key={i} className="flex flex-col text-center items-center">
              <h1 className="m-0">{stats[item as keyof typeof stats]}</h1>
              <p className="m-0" css={{ maxWidth: "80px" }}>
                {subMapKeyXf[item as keyof typeof stats]}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
